import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { TemplateContent } from "@/lib/email-template-content";
import { emailTemplateKeys } from "@/lib/query";

export interface TemplateVariable {
  name: string;
  description: string;
  sample: string;
}

export interface EmailTemplateRow {
  key: string;
  label: string;
  description: string | null;
  editable: boolean;
  isActive: boolean;
  variables: TemplateVariable[] | null;
  content: TemplateContent;
  defaultContent: TemplateContent;
  updatedAt: string | null;
  updatedBy: string | null;
}

async function failure(res: Response, fallback: string): Promise<never> {
  const body: { error?: string } | null = await res.json().catch(() => null);
  throw new Error(body?.error ?? fallback);
}

/** Every review email (the screen's rows). */
export function useEmailTemplates() {
  return useQuery({
    queryKey: emailTemplateKeys.all,
    queryFn: async (): Promise<EmailTemplateRow[]> => {
      const res = await fetch("/api/admin/email/templates");
      if (!res.ok) return failure(res, "Failed to load notification templates");
      const json: { data: EmailTemplateRow[] } = await res.json();
      return json.data.filter((t) => t.editable && t.key.startsWith("review-"));
    },
  });
}

export function useSaveTemplate(key: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (content: TemplateContent) => {
      const res = await fetch(`/api/admin/email/templates/${key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(content),
      });
      if (!res.ok) return failure(res, "Save failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
      toast.success("Template saved");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });
}

export function useSetTemplateActive(key: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { active: boolean; missed?: "send" | "skip" }) => {
      const res = await fetch(`/api/admin/email/templates/${key}/active`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(args),
      });
      if (!res.ok) return failure(res, "Could not change the switch");
      const json: { data: { isActive: boolean; skipped: number } } = await res.json();
      return json.data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.missed(key) });
      if (!result.isActive) toast.success("Switched off — nothing will be sent");
      else if (result.skipped > 0)
        toast.success(
          `Switched on — ${result.skipped} ${result.skipped === 1 ? "person" : "people"} skipped`,
        );
      else toast.success("Switched on");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not change the switch"),
  });
}

/** How many people are waiting on this email right now. */
export function useMissedCount(key: string, enabled: boolean) {
  return useQuery({
    queryKey: emailTemplateKeys.missed(key),
    queryFn: async (): Promise<number> => {
      const res = await fetch(`/api/admin/email/templates/${key}/missed`);
      if (!res.ok) return failure(res, "Could not count who is waiting");
      const json: { data: { count: number } } = await res.json();
      return json.data.count;
    },
    enabled,
    // A cached 0 must never let the admin confirm without being offered Skip.
    staleTime: 0,
    refetchOnMount: "always",
  });
}

interface TemplatePreview {
  subject: string;
  html: string;
}

/** Live preview of unsaved edits. Callers pass a debounced `content`. */
export function useTemplatePreview(key: string, content: TemplateContent, enabled: boolean) {
  return useQuery({
    queryKey: emailTemplateKeys.preview(key, JSON.stringify(content)),
    queryFn: async (): Promise<TemplatePreview> => {
      const res = await fetch(`/api/admin/email/templates/${key}/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) return failure(res, "Preview failed");
      return res.json();
    },
    enabled,
    placeholderData: (previous) => previous,
  });
}
